/**
 * PDF de la fiche de temps mensuelle signée d'un salarié.
 *
 * Agrège toutes les soumissions d'heures approuvées du mois et embarque la
 * signature numérique de chacune pour valeur d'archivage légal.
 *
 * Données = response de GET /api/employees/{id}/timesheet?year=Y&month=M
 */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const MONTHS_FR = [
  "", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const fmtDateFR = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("fr-FR");
};

const fmtTime = (t) => (t || "—").slice(0, 5);

const labelFor = (e) => {
  if (e.entry_type === "commande") {
    const ref = e.order?.reference ? `#${e.order.reference}` : "—";
    const client = e.order?.client_name ? ` (${e.order.client_name})` : "";
    return `Commande ${ref}${client}`;
  }
  if (e.entry_type === "sav") return `SAV`;
  if (e.entry_type === "atelier") return "Atelier";
  if (e.entry_type === "fournisseur") return "Fournisseur";
  return e.custom_label || "Autre";
};

export function generateTimesheetPdf(data, opts = {}) {
  const pdf = new jsPDF({ format: "a4", unit: "mm" });
  const { employee, period, submissions = [], summary = {} } = data;
  const empName = `${employee.last_name || ""} ${employee.first_name || ""}`.trim() || "—";
  const periodLabel = `${MONTHS_FR[period.month]} ${period.year}`;
  const companyName = opts.companyName || "Bati Assistant";

  // Header
  pdf.setFontSize(17);
  pdf.setFont("helvetica", "bold");
  pdf.text("Fiche de temps mensuelle", 14, 18);

  pdf.setFontSize(10);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(90);
  pdf.text(companyName, 196, 18, { align: "right" });
  pdf.setTextColor(0);

  pdf.setDrawColor(220);
  pdf.line(14, 22, 196, 22);

  // Employee + period
  pdf.setFontSize(12);
  pdf.setFont("helvetica", "bold");
  pdf.text(empName, 14, 30);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(90);
  pdf.text(`Période : ${periodLabel}`, 14, 36);
  pdf.text(`Total mensuel : ${summary.total_hours?.toFixed(1) || "0.0"} h sur ${summary.days_count || 0} jour(s)`, 14, 41);
  pdf.setTextColor(0);

  // Table de toutes les entrées agrégées
  const allRows = [];
  submissions.forEach((s) => {
    s.entries.forEach((e) => {
      allRows.push([
        fmtDateFR(e.date),
        fmtTime(e.start_time),
        fmtTime(e.end_time),
        e.hours?.toFixed(2) || "0.00",
        labelFor(e),
      ]);
    });
  });
  // Sort by date asc, then start_time
  allRows.sort((a, b) => {
    const da = a[0]; const db_ = b[0];
    if (da !== db_) return da < db_ ? -1 : 1;
    return a[1] < b[1] ? -1 : 1;
  });

  autoTable(pdf, {
    head: [["Date", "Début", "Fin", "Heures", "Affectation"]],
    body: allRows,
    startY: 48,
    margin: { left: 14, right: 14 },
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [79, 70, 229], textColor: 255 },
    foot: [["", "", "Total", summary.total_hours?.toFixed(2) || "0.00", ""]],
    footStyles: { fillColor: [241, 245, 249], textColor: 0, fontStyle: "bold" },
  });

  // Bloc signatures (1 par soumission)
  let cursorY = pdf.lastAutoTable?.finalY ? pdf.lastAutoTable.finalY + 10 : 100;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("Signatures du salarié", 14, cursorY);
  cursorY += 4;

  submissions.forEach((s, idx) => {
    if (cursorY > 250) {
      pdf.addPage();
      cursorY = 20;
    }
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(90);
    const lbl = `Soumission #${idx + 1} — signée le ${fmtDateFR(s.signed_at) || "—"} — ${s.total_hours?.toFixed(2) || "0.00"} h`;
    pdf.text(lbl, 14, cursorY + 4);
    pdf.setTextColor(0);
    if (s.signature) {
      try {
        // signature is a data URL (PNG base64)
        pdf.addImage(s.signature, "PNG", 14, cursorY + 6, 80, 24);
      } catch {
        pdf.setTextColor(180, 0, 0);
        pdf.text("(signature illisible)", 14, cursorY + 18);
        pdf.setTextColor(0);
      }
    } else {
      pdf.setTextColor(180, 0, 0);
      pdf.text("(aucune signature)", 14, cursorY + 18);
      pdf.setTextColor(0);
    }
    cursorY += 38;
  });

  // Bloc validation responsable (manuel)
  if (cursorY > 250) {
    pdf.addPage();
    cursorY = 20;
  }
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("Validation du responsable", 14, cursorY);
  cursorY += 8;
  pdf.setDrawColor(180);
  pdf.line(14, cursorY + 18, 90, cursorY + 18);
  pdf.setFontSize(9);
  pdf.setTextColor(90);
  pdf.text("Date :", 14, cursorY + 23);
  pdf.line(28, cursorY + 23, 80, cursorY + 23);
  pdf.text("Signature :", 100, cursorY + 23);
  pdf.line(122, cursorY + 23, 196, cursorY + 23);
  pdf.setTextColor(0);

  // Footer
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(140);
    pdf.text(
      `Généré le ${new Date().toLocaleDateString("fr-FR")} par ${companyName} — page ${i}/${totalPages}`,
      105, 290, { align: "center" }
    );
    pdf.setTextColor(0);
  }

  const safeName = empName.replace(/[^a-z0-9]+/gi, "_").toLowerCase();
  pdf.save(`fiche-temps_${safeName}_${period.year}-${String(period.month).padStart(2, "0")}.pdf`);
}

export default generateTimesheetPdf;
